import React from 'react'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Group from 'components/utils/Group'
import { BaseImage } from 'components/Common/OrderView/styles'
import LineUsersAltIcon from 'components/Luxkit/Icons/line/LineUsersAltIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import InlineGroup from 'components/Common/InlineGroup'
import moment from 'moment/moment'
import { SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import TextButton from 'components/Luxkit/Button/TextButton'
import { getCancellationConfig } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/utils'
import { ChatActions, MessageMetaPayload } from 'api/support'
import {
  isDateChangeAllowed,
} from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryModifyBooking/utils'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'

interface Props {
  item?: any;
  order?: any;
  offer?: any;
  roomLabel: string;
  hideSelectButton?: boolean;
  handleItemSelect?: (message: string, meta: { key: string, payload: MessageMetaPayload }) => void;
}

export default function LesaHotelItemSummary(props: Props) {
  const { item, order, offer, roomLabel, handleItemSelect, hideSelectButton = false } = props

  const reservation = item?.reservation
  const cancellationPolicy = (reservation?.cancellationPolicies ?? [])[0]
  const numberOfDaysBeforeBookingIsLockedIn = offer.daysBeforeCheckInChangesDisallowed || 0
  const changeDatesLimitReached = offer.numberOfDateChangesAllowed !== 'Unlimited' && parseInt(offer.numberOfDateChangesAllowed) <= item?.numberOfDateChanges
  const { refundable, shouldHideFreeCancellation } = getCancellationConfig({ cancellationPolicy, offer, order, reservation, hideCancellation: false })
  const hasInsurance = order.insuranceItems.length > 0
  const hasExperiences = order.addonItems.filter(item => item.offerSalesforceId === offer.id && item.status !== 'cancelled').length > 1

  const lpcChangeDatesEnabled = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.lpcChangeDatesEnabled)
  const canChangeDates = isDateChangeAllowed(false, item, reservation, item?.status === 'cancelled', order.hasFlight, numberOfDaysBeforeBookingIsLockedIn, changeDatesLimitReached, order.depositDetails, order.instalmentDetails, order.reserveForZeroDetails, shouldHideFreeCancellation, (hasExperiences || hasInsurance), lpcChangeDatesEnabled) && !item.isCompedPackage

  const onSelect = () => {
    handleItemSelect?.(item.bookingNumber, {
      key: ChatActions.SELECT_ORDER_ITEM,
      payload: {
        id_orders: order.id,
        itemId: item.id,
        bookingNumber: item.bookingNumber,
        canBeCancelled: refundable && !shouldHideFreeCancellation,
        canChangeDates,
        checkInDate: item.reservation?.startDate,
        orderType: 'hotel',
      },
    })
  }

  if (!item) {
    return null
  }

  return <LesaPurchaseSummaryContainer>
    <LesaPurchaseSummaryHeader>
      <StructureLabel variant="standard">{roomLabel}</StructureLabel>
    </LesaPurchaseSummaryHeader>
    <Group direction="vertical" gap={4}>
      <BodyTextBlock variant="medium" weight="bold">{item.package.name}</BodyTextBlock>
      <BodyTextBlock variant="medium">{item.package.roomTypeName}</BodyTextBlock>
      <InlineGroup spacing="xs" size="S" vertical="center">
        <LineUsersAltIcon colour="primary" size="XS" />
        <BodyText variant="medium" format="titlecase">
          {formatOccupantsShort(item.reservation)}
        </BodyText>
      </InlineGroup>
      <InlineGroup spacing="xs" size="S" vertical="center">
        <LineCalendarIcon size="XS" />
        <BodyText variant="medium">{moment(item.reservation?.startDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}</BodyText>
        <LineArrowRightIcon colour="neutral-four" size="XS" />
        <BodyText variant="medium">{moment(item.reservation?.endDate).format(SHORT_DAY_NAME_DAY_MONTH_YEAR)}</BodyText>
      </InlineGroup>
    </Group>
    <Group gap={24} direction="vertical">
      <BaseImage id={item.package.roomTypeImage ?? item?.offer.image.id} />
      {!hideSelectButton && <TextButton onClick={onSelect} fit="mobile-full-width" size="small" kind="secondary">Select</TextButton>}
    </Group>
  </LesaPurchaseSummaryContainer>
}
